import { Tag, Typography } from 'antd'
import { FireFilled } from '@ant-design/icons'
import dayjs from 'dayjs'
import 'dayjs/locale/ko'
import { useEffect, useMemo, useState } from 'react'
import AopsCommonModalLayout from '../../components/apos/modal/AopsCommonModalLayout'
import { getChildTasks } from '../../api/aops.api'
dayjs.locale('ko')
const { Text } = Typography

const getWorkCardTitle = (createType) => {
  switch (createType) {
    case 'STORY':
      return 'Initiative'
    case 'EPIC':
      return 'Objective'
    case 'TASK':
      return 'Task'
    default:
      return ''
  }
}

const mapProgressStateAndPriorityToFrontendColor = (
  progressState,
  priority,
) => {
  const mapInProgressPriorityToFrontendColor = (p) => {
    switch (p) {
      case 'FIRST':
        return '#FF7853'
      case 'SECOND':
        return '#FFCA00'
      case 'THIRD':
        return '#1FC297'
      default:
        return ''
    }
  }
  const mapTodoPriorityToFrontendColor = (p) => {
    switch (p) {
      case 'FIRST':
        return '#FFCCB5'
      case 'SECOND':
        return '#FFF0B5'
      case 'THIRD':
        return '#D9FCED'
      default:
        return ''
    }
  }
  return ['TODO', 'DONE', 'ON_HOLD'].includes(progressState)
    ? mapTodoPriorityToFrontendColor(priority)
    : mapInProgressPriorityToFrontendColor(priority)
}

const mapProgressStateToOpacity = (ps) =>
  ['DONE', 'ON_HOLD'].includes(ps) ? 0.5 : 1

export default function InitiativeBoardWorkPopup({
  selectedTask = null,
  weekOffset = 0,
  createType,
  handleLoadTaskDetail,
  onTaskClick,
  onClose,
  open,
  zIndex,
  onOpen,
}) {
  const [childTasks, setChildTasks] = useState([])

  const fetchChildTasks = async () => {
    getChildTasks(selectedTask.worksInfoDto.id).then((res) => {
      // API 응답 구조에 맞게 안전하게 처리
      const works = res?.works || res || []
      setChildTasks(Array.isArray(works) ? works : Object.values(works))
    })
  }

  useEffect(() => {
    if (open && selectedTask?.worksInfoDto?.id) {
      fetchChildTasks()
      if (onOpen) onOpen() // 열릴 때 부모에게 알림
    } else {
      setChildTasks([])
    }
  }, [selectedTask, open, onOpen])

  const COL_WIDTH_PERCENT = 20

  // 1. 2주치 데이터 계산 로직 (기존과 동일)
  const weeksData = useMemo(() => {
    return [0, 1].map((offset) => {
      const currentWeekOffset = weekOffset + offset
      const monday = dayjs()
        .startOf('week')
        .add(1, 'day')
        .add(currentWeekOffset, 'week')
        .startOf('day')
      const weekEndFriday = monday.add(4, 'day').endOf('day')

      const days = [...Array(5)].map((_, i) => {
        const day = monday.add(i, 'day')
        return {
          date: day.format('M/D'),
          dayName: day.format('ddd'),
          fullDate: day.format('YYYY-MM-DD'),
          isToday: day.format('YYYY-MM-DD') === dayjs().format('YYYY-MM-DD'),
        }
      })

      const weekTasks = childTasks
        .filter((item) => {
          if (!item?.worksInfoDto) return false
          const taskStart = dayjs(item.worksInfoDto.startDateTime)
          const taskEnd = dayjs(item.worksInfoDto.endDateTime)
          return (
            taskStart.isBefore(weekEndFriday) &&
            taskEnd.isAfter(monday.subtract(1, 'ms'))
          )
        })
        .map((item) => {
          const info = item.worksInfoDto
          const taskStart = dayjs(info.startDateTime)
          const taskEnd = dayjs(info.endDateTime)
          const displayStart = taskStart.isBefore(monday) ? monday : taskStart
          const displayEnd = taskEnd.isAfter(weekEndFriday)
            ? weekEndFriday
            : taskEnd

          return {
            ...item,
            startCol: displayStart.diff(monday, 'day'),
            span: displayEnd.diff(displayStart, 'day') + 1,
            isContinuedFromLastWeek: taskStart.isBefore(monday),
            isContinuedToNextWeek: taskEnd.isAfter(weekEndFriday),
            color: mapProgressStateAndPriorityToFrontendColor(
              info.progressState,
              info.priority,
            ),
            opacity: mapProgressStateToOpacity(info.progressState),
          }
        })

      return { days, weekTasks, monday }
    })
  }, [childTasks, weekOffset])

  return (
    <AopsCommonModalLayout
      title={`${getWorkCardTitle(createType)} 내 Task 목록`}
      onClose={onClose}
      open={open}
      width="50%"
      zIndex={zIndex || 1000} // 기본값을 1000으로 설정 (Antd 기본값과 동일)
    >
      {/* 뎁스를 낮춰 전체 배경을 흰색으로 통일한 메인 컨테이너 */}
      <div
        style={{
          backgroundColor: '#FFFFFF',
          padding: '24px 32px',
          borderRadius: '12px',
          margin: '0 32px',
        }}
      >
        {weeksData.map((week, weekIdx) => (
          <div key={weekIdx} style={{ marginBottom: weekIdx === 0 ? 32 : 0 }}>
            {/* 1. 주간 헤더 (배경을 흰색으로 변경하여 일체감 형성) */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(5, 1fr)',
                borderRadius: '12px',
                marginBottom: '16px',
                border: '1px solid #Eef3f6', // 테두리만 얇게 유지
                backgroundColor: '#F6F7FA',
              }}
            >
              {week.days.map((d) => (
                <div
                  key={d.fullDate}
                  style={{ padding: '12px 0', textAlign: 'center' }}
                >
                  <div
                    style={{
                      display: 'inline-flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '50px',
                      height: '50px',
                      borderRadius: '12px',
                      background: d.isToday ? '#383E44' : 'transparent',
                    }}
                  >
                    <Text
                      style={{
                        fontSize: '15px',
                        color: d.isToday ? '#FFFFFF' : '#191A1C',
                        fontWeight: '700',
                      }}
                    >
                      {d.date}
                    </Text>
                    <Text
                      style={{
                        fontSize: '12px',
                        color: d.isToday ? '#FFFFFF' : '#6d747a',
                        fontWeight: '500',
                      }}
                    >
                      {d.dayName}
                    </Text>
                  </div>
                </div>
              ))}
            </div>

            {/* 2. 보드 본문 (그리드 + 태스크) */}
            <div style={{ position: 'relative', minHeight: '140px' }}>
              {/* 배경 그리드 선 */}
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'grid',
                  gridTemplateColumns: 'repeat(5, 1fr)',
                  pointerEvents: 'none',
                }}
              >
                {[...Array(5)].map((_, i) => (
                  <div
                    key={i}
                    style={{
                      borderRight: i === 4 ? 'none' : '1px solid #F0F2F5',
                    }}
                  />
                ))}
              </div>

              {/* 태스크 카드 렌더링 */}
              <div
                style={{
                  position: 'relative',
                  zIndex: 1,
                  paddingBottom: '16px',
                }}
              >
                {week.weekTasks.map((task) => (
                  <div
                    key={`${task.worksInfoDto.id}-${weekIdx}`}
                    onClick={() => {
                      onTaskClick?.(task)
                      fetchChildTasks()
                    }}
                    style={{
                      position: 'relative',
                      marginLeft: `${task.startCol * COL_WIDTH_PERCENT}%`,
                      width: `${task.span * COL_WIDTH_PERCENT}%`,
                      height: '36px',
                      background: '#FFFFFF',
                      boxShadow: '2px 2px 8px rgba(0, 0, 0, 0.04)',
                      borderRadius: '10px',
                      borderTopLeftRadius: task.isContinuedFromLastWeek
                        ? '0px'
                        : '10px',
                      borderBottomLeftRadius: task.isContinuedFromLastWeek
                        ? '0px'
                        : '10px',
                      borderTopRightRadius: task.isContinuedToNextWeek
                        ? '0px'
                        : '10px',
                      borderBottomRightRadius: task.isContinuedToNextWeek
                        ? '0px'
                        : '10px',
                      border: '1px solid #E2EAEF',
                      padding: '0 12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: '8px',
                      cursor: 'pointer',
                      opacity: task.opacity,
                      boxSizing: 'border-box',
                    }}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '3px',
                        background: '#E2EAEF',
                        overflow: 'hidden',
                        borderRadius: '10px 10px 0 0',
                      }}
                    >
                      <div
                        style={{
                          width: `${task.totalChildCount > 0 ? (task.completedChildCount / task.totalChildCount) * 100 : 100}%`,
                          height: '100%',
                          backgroundColor: task.color,
                        }}
                      />
                    </div>
                    <Text
                      strong
                      style={{
                        fontSize: '12px',
                        color: '#191A1C',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {task.worksInfoDto.title}
                    </Text>
                    {(task?.worksInfoDto?.hasProblem ||
                      task?.worksInfoDto?.storyChildProblemCount > 0) && (
                      <Tag
                        color={
                          task?.worksInfoDto?.problemState ===
                          'RESOLVED_OVERDUE_REVIEW_TIME'
                            ? '#6D747A'
                            : '#F5222D'
                        }
                        icon={<FireFilled />}
                        style={{
                          marginRight: 0,
                          fontSize: '10px',
                          padding: '0 4px',
                          height: '20px',
                          display: 'flex',
                          alignItems: 'center',
                          color:
                            task?.worksInfoDto?.problemState ===
                            'RESOLVED_OVERDUE_REVIEW_TIME'
                              ? '#6D747A'
                              : '#F25A67',
                          border:
                            task?.worksInfoDto?.problemState ===
                            'RESOLVED_OVERDUE_REVIEW_TIME'
                              ? '1px solid #6D747A'
                              : '1px solid #F25A67',
                          backgroundColor: '#fff',
                        }}
                      ></Tag>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* 주차 사이 구분선 (이미지 스타일 참고) */}
            {weekIdx === 0 && (
              <div
                style={{
                  height: '1px',
                  background: '#Eef3f6',
                  margin: '16px 0 24px 0',
                }}
              />
            )}
          </div>
        ))}
      </div>
    </AopsCommonModalLayout>
  )
}
